/**
 * Registration State Management Hook
 *
 * CRITICAL RACE CONDITION FIX:
 * This hook previously suffered from a race condition where localStorage would be
 * wiped on page refresh. The issue occurred because:
 *
 * 1. Page loads → State initializes to defaults (blank values)
 * 2. Components mount → Some called updateRegistration() with partial data
 * 3. updateRegistration() → Saved blank/partial state to localStorage
 * 4. Hydration starts → Loaded from localStorage, but it was already wiped!
 *
 * THE FIX:
 * - Added `initialized` flag to prevent localStorage saves before hydration completes
 * - Moved localStorage save outside the forEach loop to save once after all updates
 * - Removed falsy value check that was preventing saves with empty strings/false values
 * - Added proper price plan dependency checks to ensure mapping functions are ready
 *
 * HYDRATION ORDER (critical for data integrity):
 * 1. Price plans API → Must complete first (for employee count mapping)
 * 2. localStorage → User's saved progress (primary source)
 * 3. Cookies → Marketing/tracking data (overlay)
 * 4. API → Registered user data (highest priority overlay)
 * 5. Mark initialized → Enables localStorage saves for future updates
 */
import { reactive, readonly, ref, watch } from '@nuxtjs/composition-api';
import { getFromLocalStorage, saveInLocalStorage } from '@/utils/localStorage';
import useLeadService from '@/modules/SelfServiceRegistration/hooks/useLeadService';
import Registration from '@/services/Registration';
import Cookie from 'js-cookie';
import CookieNames from '@/constants/CookieNames';
import { QueryParamNames } from '@/constants/QueryParams';
import usePricePlans from '@/hooks/usePricePlans';
import useResumeSession from './useResumeSession';

let instantiated = false;
const initialized = ref(false);
let getAppropriatePlanByEmployeeRange = () => {};
let getPlanById = () => {};
let getEmployeeRangeByTier = () => {};
let getLead = () => {};

const { resumeSession } = useResumeSession();

export const CALL_STATES = Object.freeze({
  CALL_CONFIRMED: 'call-confirmed',
  CALL_ME_NOW: 'call-me-now',
});

export const RegistrationStateNames = Object.freeze({
  HAS_VALID_TOKEN: 'hasValidToken',
  // Company Info
  COMPANY_ID: 'companyId',
  COMPANY_CREATED_AT: 'companyCreatedAt',
  COMPANY_CONVERTED_AT: 'companyConvertedAt',
  COMPANY_NAME: 'name',
  INDUSTRY: 'industry',

  // Plan Info
  BILLING_PLAN_ID: 'billingPlanId',
  PLAN_ID: 'plan',
  TIER_NUMBER: 'tierNumber',
  NUM_EMPLOYEES: 'numOfEmployees',
  NUM_EMPLOYEES_FREE_TEXT: 'employeeSizeFreeText',

  // User Info
  USER_ID: 'userId',
  FIRST_NAME: 'firstName',
  LAST_NAME: 'lastName',
  FULL_NAME: 'fullName',
  PHONE: 'phone',
  EMAIL: 'email',
  ZIP: 'zip',
  STATE: 'state',
  PASSWORD: 'password',

  // Marketing Info
  HOW_HEARD: 'howDidYouHear',
  STATION: 'station',
  FUNNEL: 'funnel',
  PARTNER_STACK_ID: 'partnerStackId',

  // Misc
  PAGE_REGISTERED_ON: 'pageRegisteredOn',
  SELF_SIGNUP: 'selfSignup',
  HRMID: 'hrmId',
  NEWLEAD: 'isNewLead',
  CALLTYPE: 'callType',
  CALLSTARTTIME: 'callStartTime',
  CALLENDTIME: 'callEndTime',
  WHAT_BROUGHT_YOU_TO_BAMBEE: 'what_brought_you_to_bambee',
  OFFER_CREATED_AT: 'offerCreatedAt',
  LEAD_DEVICE_TYPE: 'leadDeviceType',
  PAYMENT_PAGE_EXPERIENCE: 'checkoutExperience',

  PURCHASED_PLAN_ID: 'purchasedPlan',
  PAYMENT_METHOD: 'paymentMethod',
  BILLING_PERIOD: 'billingPeriod',

  // Post-purchase (BIO-2149): the coverage level the hand-off questions picked and the
  // plan confirmed on the plan screen, as shown (value, productKey, monthlyPrice, band).
  SUPERVISION_LEVEL: 'supervisionLevel',
  SUPERVISION_PLAN: 'supervisionPlan',
  // 'scheduled' once Calendly reports the founder call booked, 'skipped' if deferred.
  FOUNDER_CALL: 'founderCall',
});

const state = reactive({
  [RegistrationStateNames.HAS_VALID_TOKEN]: false,
  [RegistrationStateNames.COMPANY_ID]: '',
  [RegistrationStateNames.USER_ID]: '',
  [RegistrationStateNames.PAGE_REGISTERED_ON]: '',
  [RegistrationStateNames.FIRST_NAME]: '',
  [RegistrationStateNames.LAST_NAME]: '',
  [RegistrationStateNames.FULL_NAME]: '',
  [RegistrationStateNames.PHONE]: '',
  [RegistrationStateNames.EMAIL]: '',
  [RegistrationStateNames.COMPANY_NAME]: '',
  [RegistrationStateNames.INDUSTRY]: '',
  [RegistrationStateNames.NUM_EMPLOYEES]: '',
  [RegistrationStateNames.ZIP]: '',
  [RegistrationStateNames.PLAN_ID]: null,
  [RegistrationStateNames.TIER_NUMBER]: null,
  [RegistrationStateNames.HOW_HEARD]: '',
  [RegistrationStateNames.NUM_EMPLOYEES_FREE_TEXT]: '',
  [RegistrationStateNames.SELF_SIGNUP]: true,
  [RegistrationStateNames.STATE]: '',
  [RegistrationStateNames.HRMID]: '',
  [RegistrationStateNames.FUNNEL]: '',
  [RegistrationStateNames.STATION]: '',
  [RegistrationStateNames.NEWLEAD]: false,
  [RegistrationStateNames.CALLTYPE]: '',
  [RegistrationStateNames.CALLSTARTTIME]: '',
  [RegistrationStateNames.CALLENDTIME]: '',
  [RegistrationStateNames.WHAT_BROUGHT_YOU_TO_BAMBEE]: '',
  [RegistrationStateNames.COMPANY_CREATED_AT]: '',
  [RegistrationStateNames.COMPANY_CONVERTED_AT]: '',
  [RegistrationStateNames.OFFER_CREATED_AT]: '',
  [RegistrationStateNames.PASSWORD]: '',
  [RegistrationStateNames.LEAD_DEVICE_TYPE]: '',
  [RegistrationStateNames.PARTNER_STACK_ID]: '',
  [RegistrationStateNames.PAYMENT_PAGE_EXPERIENCE]: '',
  [RegistrationStateNames.BILLING_PLAN_ID]: '',
  [RegistrationStateNames.PURCHASED_PLAN_ID]: '',
  [RegistrationStateNames.PAYMENT_METHOD]: 'CARD',
  [RegistrationStateNames.BILLING_PERIOD]: '',
  [RegistrationStateNames.SUPERVISION_LEVEL]: '',
  [RegistrationStateNames.SUPERVISION_PLAN]: null,
  [RegistrationStateNames.FOUNDER_CALL]: '',
});

const LOCAL_STORAGE_REGISTRATION_KEY = 'reg_state';

/**
 * Updates registration state with new data and handles localStorage persistence.
 *
 * CRITICAL: This function includes race condition protection to prevent early
 * component initialization from wiping localStorage before hydration completes.
 *
 * @param {Object} toSave - Key-value pairs to update in state
 * @param {boolean} saveToLocalStorage - Whether to persist to localStorage
 */
const updateRegistration = (toSave = {}, saveToLocalStorage = true) => {
  Object.entries(toSave).forEach(([key, val]) => {
    if (!(key in state)) {
      return;
    }

    state[key] = val;

    // Auto-map employee count to appropriate plan (requires price plans API)
    if (key === RegistrationStateNames.NUM_EMPLOYEES) {
      if (getAppropriatePlanByEmployeeRange && typeof getAppropriatePlanByEmployeeRange === 'function') {
        const targetPlan = getAppropriatePlanByEmployeeRange(val);
        const planId = targetPlan?.value || null;
        const tierNumber = targetPlan?.tierNumber || null;
        state[RegistrationStateNames.PLAN_ID] = planId;
        state[RegistrationStateNames.TIER_NUMBER] = tierNumber;
      }
    }

    // Auto-map plan ID to tier/employee data (requires price plans API)
    if (key === RegistrationStateNames.PLAN_ID && val) {
      if (getPlanById && typeof getPlanById === 'function') {
        const plan = getPlanById(val);
        if (plan) {
          // Now account for lite plans that do not have tier number or employee sizing
          if (
            plan.tierNumber !== undefined ||
            plan.employeeSizeMapping !== undefined
          ) {
            state[RegistrationStateNames.TIER_NUMBER] = plan.tierNumber;
            state[RegistrationStateNames.NUM_EMPLOYEES] =
              plan.employeeSizeMapping;
          }
        } else {
          // Fallback: extract tier from plan ID string (brittle but necessary)
          const brittleTierNumber = val.split('_t').at(-1) || '';
          if (brittleTierNumber && getEmployeeRangeByTier && typeof getEmployeeRangeByTier === 'function') {
            const evenMoreBrittleTier = Number(brittleTierNumber);
            const eeRange = getEmployeeRangeByTier(evenMoreBrittleTier);
            state[RegistrationStateNames.TIER_NUMBER] = evenMoreBrittleTier;
            state[RegistrationStateNames.NUM_EMPLOYEES] = eeRange;
          }
        }
      }
    }
  });

  if (saveToLocalStorage) {
    // CRITICAL RACE CONDITION PROTECTION:
    // Prevent localStorage saves before hydration completes to avoid wiping saved data.
    // Components may call updateRegistration during initialization with partial data,
    // which would overwrite the complete state we're trying to restore from localStorage.
    if (!initialized.value) {
      return;
    }
    saveInLocalStorage(LOCAL_STORAGE_REGISTRATION_KEY, JSON.stringify(state));
  }
};

/**
 * Hydrates state from cookie data (marketing/tracking info).
 * This overlays cookie values onto existing state without replacement.
 */
const hydrateFromCookies = () => {
  const cookieQueryStr = Cookie.get(CookieNames.QUERY_PARAMS) || '{}';
  const cookieQuery = JSON.parse(cookieQueryStr);
  const partnerStackIdCookie = Cookie.get(CookieNames.PARTNER_STACK_ID) || '';
  const funnelCookie = Cookie.get(CookieNames.FUNNEL) || '';
  const toUpdate = {
    [RegistrationStateNames.PAYMENT_PAGE_EXPERIENCE]:
      cookieQuery[QueryParamNames.PAYMENT_PAGE_EXPERIENCE] || '',
    [RegistrationStateNames.PARTNER_STACK_ID]: partnerStackIdCookie,
    [RegistrationStateNames.FUNNEL]: funnelCookie,
  };

  updateRegistration(toUpdate);
};

/**
 * Hydrates state from localStorage (user's progress from previous session).
 * This is the primary source of truth for preserving user progress across refreshes.
 */
const hydrateFromLocalStorage = () => {
  const stateInStorage = getFromLocalStorage(LOCAL_STORAGE_REGISTRATION_KEY);
  const newState = (stateInStorage && JSON.parse(stateInStorage)) || {};
  // Don't save back to localStorage since we're loading FROM localStorage
  updateRegistration(newState, false);
};

/**
 * Hydrates state from API (registered user data - highest priority).
 * This only runs for users with valid tokens and overlays API data as source of truth.
 */
const hydrateFromAPI = async () => {
  const APIUserData = await getLead();
  let userData = APIUserData?.data;

  if (!userData) {
    // Attempt to resume from information in cookies
    userData = await resumeSession();

    if (!userData) {
      updateRegistration({
        [RegistrationStateNames.HAS_VALID_TOKEN]: false,
      });
      return;
    }
  }

  const {
    _company: {
      profile: companyProfile,
      name: companyName,
      plan: planId,
      id: companyId,
      marketing: companyMarketing,
      converted_at: companyConvertedAt,
    },
    _auth: auth,
    profile,
    _id,
  } = userData || {};

  const apiData = {
    [RegistrationStateNames.HAS_VALID_TOKEN]: true,
    [RegistrationStateNames.FIRST_NAME]: profile.first_name,
    [RegistrationStateNames.LAST_NAME]: profile.last_name,
    [RegistrationStateNames.FULL_NAME]: profile.full_name,
    [RegistrationStateNames.PHONE]: profile.phone,
    [RegistrationStateNames.EMAIL]: auth.email,
    [RegistrationStateNames.COMPANY_NAME]: companyName,
    [RegistrationStateNames.INDUSTRY]: companyProfile.industry,
    [RegistrationStateNames.ZIP]: companyProfile.zip,
    [RegistrationStateNames.STATE]: companyProfile.state,
    [RegistrationStateNames.PLAN_ID]: planId,
    [RegistrationStateNames.COMPANY_ID]: companyId,
    [RegistrationStateNames.USER_ID]: _id,
    [RegistrationStateNames.PURCHASED_PLAN_ID]: !!companyConvertedAt
      ? planId
      : '',
    [RegistrationStateNames.WHAT_BROUGHT_YOU_TO_BAMBEE]:
      companyMarketing?.howDidYouHear ||
      state[RegistrationStateNames.WHAT_BROUGHT_YOU_TO_BAMBEE] ||
      'n/a',
  };

  updateRegistration(apiData);
};

/**
 * Main hydration function that restores user state from multiple sources.
 *
 * CRITICAL: This runs in a specific order to ensure proper data priority:
 * 1. Price plans must be ready (for employee count -> plan mapping)
 * 2. Load from localStorage (user's saved progress)
 * 3. Overlay from cookies (marketing/tracking data)
 * 4. Overlay from API (registered user data - highest priority)
 *
 * Race condition protection: No localStorage saves occur until this completes.
 */
const hydrateProgress = async () => {
  // Ensure price plans are available before any hydration
  // This prevents broken employee count -> plan ID mappings
  if (!getAppropriatePlanByEmployeeRange || typeof getAppropriatePlanByEmployeeRange !== 'function') {
    return;
  }

  // Hydrate in priority order: localStorage -> cookies -> API
  hydrateFromLocalStorage();
  hydrateFromCookies();
  await hydrateFromAPI();

  // Mark as initialized - this enables localStorage saves for future updates
  initialized.value = true;
};

// Returns a promise that resolves to the user object
const register = async (toSave = {}) => {
  const user = await Registration.register(toSave).catch((error) => {
    console.error(error);
    throw new Error(error);
  });

  if (!user) {
    return;
  }

  updateRegistration({
    [RegistrationStateNames.HAS_VALID_TOKEN]: true,
    [RegistrationStateNames.COMPANY_ID]: user._company.id,
    [RegistrationStateNames.COMPANY_CREATED_AT]: user._company.created_at,
  });

  return user;
};

const useRegistration = () => {
  // For Singleton patterns, where we only want 1 source of truth
  if (!instantiated) {
    instantiated = true;
    const {
      getAppropriatePlanByEmployeeRange:
        getAppropriatePlanByEmployeeRangeOriginal,
      getPlanById: getPlanByIdOriginal,
      getEmployeeRangeByTier: getEmployeeRangeByTierOriginal,
      isReady: isPlansReady,
    } = usePricePlans();

    const { getLead: getLeadOriginal } = useLeadService();
    getLead = getLeadOriginal;

    getAppropriatePlanByEmployeeRange =
      getAppropriatePlanByEmployeeRangeOriginal;
    getPlanById = getPlanByIdOriginal;
    getEmployeeRangeByTier = getEmployeeRangeByTierOriginal;

    const unwatch = watch(
      isPlansReady,
      (n) => {
        if (n) {
          hydrateProgress();
          unwatch && unwatch();
        }
      },
      { immediate: true }
    );
  }

  return {
    state: readonly(state),
    initialized,
    updateRegistration,
    hydrateProgress,
    register,
  };
};

export default useRegistration;
